import React, { useEffect, useMemo, useState } from 'react';

import { ExpLabel } from '@exp/components/localization';
import { useIsMediaMD } from '@exp/hooks/useMediaQuery';

import PerksBanner from '../components/PerksBanner';
import VetTeasers from '../components/VetsTeasers';
import Modes, { ModeConfig, ModeContext } from '../constants/modes';
import {
  BackdropStage,
  BackdropTabs,
  clearBackdrop,
  selectBackdrop,
  selectBackdropTab,
  selectConfig,
  selectContext,
  selectShowManageBackdrop,
  setBackdrop,
} from '../redux/context';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useSendEvent } from '../utilities/useAnalytics';
import { isVetCode, noop } from '../utilities/utilities';
import { TemplateProps } from './types';
import Backdrop from './Backdrop';

import './staticLayoutV3.less';

const AnalyticEvents = {
  ONBOARDING_RETURN_TO_SETUP_CLICK: 'ONBOARDING_RETURN_TO_SETUP_CLICK',
  ONBOARDING_QUALIFY_BACK_CLICK: 'ONBOARDING_QUALIFY_BACK_CLICK',
};

const StaticLayoutV3 = React.forwardRef<HTMLDivElement, TemplateProps>(
  ({ children = null, parent = null, onPrevious = noop }, ref) => {
    const dispatch = useAppDispatch();
    const config: ModeConfig = useAppSelector(selectConfig);
    const { affiliations, groupCodeResult, mode }: ModeContext = useAppSelector(selectContext);
    const sendEvent = useSendEvent({ mode });
    const stage = useAppSelector(selectBackdrop);
    const backdropTab = useAppSelector(selectBackdropTab);
    // The mobile tab classes only mean anything while the manage backdrop's tab
    // strip is on screen. Emitting them otherwise can hide the content pane with
    // no control to bring it back (TPF-65908).
    const showManageBackdrop = useAppSelector(selectShowManageBackdrop);
    const { groupCode } = groupCodeResult || {}; // TODO: Clean up all this vets stuff
    const isVAPass = useMemo(() => isVetCode(groupCode), [groupCode]);
    const isMD = useIsMediaMD();
    const [headerHeight, setHeaderHeight] = useState(0);
    const [footerHeight, setFooterHeight] = useState(0);

    useEffect(() => {
      const measure = () => {
        const navHeight = (document.getElementsByClassName('ev-nav-content')?.[0] as HTMLElement)?.offsetHeight || 0;
        const footerEl = (document.getElementById('exp-core-footer') ||
          document.getElementsByClassName('core-footer')?.[0]) as HTMLElement | undefined;
        setHeaderHeight(navHeight);
        setFooterHeight(footerEl?.offsetHeight || 0);
      };
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }, []);

    useEffect(() => {
      if (config.mode === Modes.qualify.name) {
        dispatch(setBackdrop(BackdropStage.qualify));
      } else if (config.mode === Modes.manageTeams.name) {
        dispatch(setBackdrop(BackdropStage.manage));
      } else {
        dispatch(setBackdrop(BackdropStage.postAffiliation));
      }
      return () => {
        dispatch(clearBackdrop());
      };
    }, [dispatch, config.mode]);

    const { hasPending, hasAffiliated } = useMemo(
      () => ({
        hasPending: affiliations?.some((a) => a.affiliationStatus === 'PENDING'),
        hasAffiliated: affiliations?.some((a) => a.affiliationStatus === 'ACTIVE'),
      }),
      [affiliations],
    );

    return (
      <div
        className={`exp-onboarding-full-page ${stage} ${showManageBackdrop ? backdropTab : BackdropTabs.qualify}`}
        ref={ref}
        style={
          {
            '--exp-header-height': `${headerHeight}px`,
            '--exp-footer-height': `${footerHeight}px`,
          } as React.CSSProperties
        }
      >
        <Backdrop stepCount={2} completedCount={hasAffiliated ? 2 : hasPending ? 1 : 0} />
        <div className="exp-onboarding-content">
          {parent?.previousPage?.replayable ? (
            <div className="exp-onb-back-btn-wrap">
              <button
                className="exp-onb-back-btn btn-wp-reset link-primary"
                onClick={() => {
                  sendEvent(AnalyticEvents.ONBOARDING_QUALIFY_BACK_CLICK);
                  onPrevious();
                }}
                type="button"
              >
                <i className="exp-ux-small exp-ux-arrow" />
                <ExpLabel defaultMessage="Back" messageKey="onb.qualify.back" />
              </button>
            </div>
          ) : null}
          {children}
          {isVAPass && !isMD && <VetTeasers />}
          {config.mode === Modes.qualify.name && (
            <div className="flow-actions">
              <a
                className="btn-outline setup"
                href={`${config.setupDestination || '/user/setup'}?${config.urlParamPrefix || 'onb_'}editSetupMode=true`}
                onClick={() => {
                  sendEvent(AnalyticEvents.ONBOARDING_RETURN_TO_SETUP_CLICK);
                }}
              >
                <i className="exp-ux-arrow exp-ux-small" />
                <ExpLabel defaultMessage="Set Up" messageKey="onb.accordion.setup" />
              </a>
            </div>
          )}
        </div>
        {config.mode === Modes.postAffiliation.name && <PerksBanner />}
      </div>
    );
  },
);

StaticLayoutV3.displayName = 'StaticLayoutV3';

export default StaticLayoutV3;
